import { useMemo, useCallback } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { useCurrentGame } from '../context/CurrentGameContext';

interface GameTimeFormatterOptions {
  /**
   * Format type for the date
   * - 'short': Just the date (e.g., "Jan 15")
   * - 'long': Full date with time (default)
   * - 'time-only': Just the time (e.g., "3:30 PM")
   */
  formatType?: 'short' | 'long' | 'time-only';

  /**
   * Whether to show relative dates like "Today", "Tomorrow", "Yesterday"
   * @default true
   */
  showRelative?: boolean;

  /**
   * Whether to show timezone abbreviation (e.g., "EST")
   * Only shown when the timezone differs from browser timezone
   * @default true
   */
  showTimezone?: boolean;
}

const TIME_FORMATS = {
  SHORT_DATE: 'MMM dd',
  FULL_DATE_TIME: 'MMM dd, yyyy • h:mmaaa',
  FULL_DATE_TIME_TZ: 'MMM dd, yyyy • h:mmaaa zzz',
  TIME_ONLY: 'h:mm a zzz',
} as const;

/**
 * Custom hook that provides time formatting functions for the game page
 *
 * Always uses the venue's timezone when available, falls back to browser timezone.
 *
 * @example
 * ```tsx
 * const { formatGameTime, getActiveTimezone } = useGameTimeFormatter();
 *
 * // Format a date
 * const formattedDate = formatGameTime(gameStartTime);
 *
 * // Get the current active timezone
 * const timezone = getActiveTimezone(); // Always venue timezone when available
 * ```
 */
export const useGameTimeFormatter = () => {
  const { game } = useCurrentGame();

  const browserTimezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    []
  );

  /**
   * Always uses venue timezone when available, falls back to browser timezone
   */
  const activeTimezone = useMemo(() => {
    // Always use venue timezone if available
    if (game?.venue?.timezone) {
      return game.venue.timezone;
    }

    // Fallback to browser timezone if venue timezone is not available
    return browserTimezone;
  }, [game?.venue?.timezone, browserTimezone]);

  /**
   * Checks if a date is valid
   */
  const isValidDate = (date: any): date is Date => {
    return date instanceof Date && !isNaN(date.getTime());
  };

  /**
   * Gets relative day format (Today, Tomorrow, Yesterday)
   * This is timezone-aware - it compares dates in the target timezone, not browser timezone
   */
  const getRelativeDayFormat = (targetDate: Date, tz: string, includeTime: boolean) => {
    try {
      // Get current date in the TARGET timezone
      const nowInTz = formatInTimeZone(new Date(), tz, 'yyyy-MM-dd');
      const targetInTz = formatInTimeZone(targetDate, tz, 'yyyy-MM-dd');

      // Calculate tomorrow and yesterday in the target timezone
      const tomorrowDate = new Date();
      tomorrowDate.setDate(tomorrowDate.getDate() + 1);
      const tomorrowInTz = formatInTimeZone(tomorrowDate, tz, 'yyyy-MM-dd');

      const yesterdayDate = new Date();
      yesterdayDate.setDate(yesterdayDate.getDate() - 1);
      const yesterdayInTz = formatInTimeZone(yesterdayDate, tz, 'yyyy-MM-dd');

      if (targetInTz === nowInTz) {
        return includeTime
          ? `Today • ${formatInTimeZone(targetDate, tz, TIME_FORMATS.TIME_ONLY)}`
          : 'Today';
      }
      if (targetInTz === tomorrowInTz) {
        return includeTime
          ? `Tomorrow • ${formatInTimeZone(targetDate, tz, TIME_FORMATS.TIME_ONLY)}`
          : 'Tomorrow';
      }
      if (targetInTz === yesterdayInTz) {
        return includeTime
          ? `Yesterday • ${formatInTimeZone(targetDate, tz, TIME_FORMATS.TIME_ONLY)}`
          : 'Yesterday';
      }
      return null;
    } catch (error) {
      console.error('Error in getRelativeDayFormat:', error);
      return null;
    }
  };

  /**
   * Formats a date/time for display in the game page
   *
   * @param date - The date to format
   * @param options - Formatting options
   * @returns Formatted date string
   */
  const formatGameTime = useCallback(
    (
      date: Date | string | null | undefined,
      options: GameTimeFormatterOptions = {}
    ): string => {
      const {
        formatType = 'long',
        showRelative = true,
        showTimezone = true,
      } = options;

      // Handle null/undefined
      if (!date) return '';

      // Convert string to Date if needed
      const targetDate = typeof date === 'string' ? new Date(date) : date;

      // Validate date
      if (!isValidDate(targetDate)) {
        console.error('Invalid date provided to formatGameTime:', date);
        return '';
      }

      try {
        const tz = activeTimezone;

        // Show relative dates if enabled and not time-only format
        if (showRelative && formatType !== 'time-only') {
          const relativeFormat = getRelativeDayFormat(
            targetDate,
            tz,
            formatType !== 'short'
          );
          if (relativeFormat) return relativeFormat;
        }

        // Determine format string based on options
        let formatString: string;

        if (formatType === 'short') {
          formatString = TIME_FORMATS.SHORT_DATE;
        } else if (formatType === 'time-only') {
          formatString = TIME_FORMATS.TIME_ONLY;
        } else {
          // Show timezone abbreviation only if timezone differs from browser and showTimezone is true
          const shouldShowTz = showTimezone && browserTimezone !== tz;
          formatString = shouldShowTz
            ? TIME_FORMATS.FULL_DATE_TIME_TZ
            : TIME_FORMATS.FULL_DATE_TIME;
        }

        return formatInTimeZone(targetDate, tz, formatString);
      } catch (error) {
        console.error('Error in formatGameTime:', error);
        return '';
      }
    },
    [activeTimezone, browserTimezone]
  );

  /**
   * Returns the currently active timezone
   * Useful for displaying timezone info or passing to other functions
   */
  const getActiveTimezone = useCallback(() => activeTimezone, [activeTimezone]);

  /**
   * Returns whether the venue timezone is being used (always true when venue has timezone)
   */
  const isUsingVenueTimezoneFunc = useCallback(
    () => Boolean(game?.venue?.timezone),
    [game?.venue?.timezone]
  );

  /**
   * Returns the venue timezone name if available
   */
  const getVenueTimezone = useCallback(
    () => game?.venue?.timezone || null,
    [game?.venue?.timezone]
  );

  return {
    formatGameTime,
    getActiveTimezone,
    isUsingVenueTimezone: isUsingVenueTimezoneFunc,
    getVenueTimezone,
    browserTimezone,
  };
};
